import { computed, ref, watch, onScopeDispose } from 'vue';
import { useIntersectionObserver } from '@vueuse/core';
import { useSession } from '#imports';
import { useAuthState } from '~/composables/useAuthState';
import { usePagination } from '~/composables/usePagination';
import { musicService, type MusicTask } from '~/services/musicService';

type LibraryFilters = { search: string; status: string };
const statuses = ['all', 'processing', 'completed', 'failed'];

/** Only UI query parameters are restored; the existing private API contract is retained. */
export function readMusicLibraryFilters(query: Record<string, unknown>): LibraryFilters {
  const search = typeof query.q === 'string' && query.q.length <= 200 ? query.q.trim() : '';
  const status = typeof query.status === 'string' && statuses.includes(query.status) ? query.status : 'all';
  return { search, status };
}

export function useMusicLibraryState(initial: LibraryFilters = { search: '', status: 'all' }) {
  const { user, isAuthenticated, isAuthReady, fetchAndStoreToken } = useAuthState();
  const { session } = useSession();
  const filters = ref(initial);
  const searchInput = ref(initial.search);
  const submittedSearch = computed(() => filters.value.search);
  const status = computed({
    get: () => filters.value.status,
    set: value => { if (statuses.includes(value) && value !== filters.value.status) filters.value = { ...filters.value, status: value }; },
  });
  const ownerIdentity = computed(() => JSON.stringify([isAuthReady.value, isAuthenticated.value, user.value?.id || '', session.value?.id || '']));
  const identity = computed(() => JSON.stringify([ownerIdentity.value, filters.value.search, filters.value.status]));
  const canRead = computed(() => isAuthReady.value && isAuthenticated.value && !!user.value?.id && !!session.value?.id);
  let disposed = false;
  let controller: AbortController | undefined;
  const requestedPage = ref(1);
  const pagination = usePagination<MusicTask>(async page => {
    const key = identity.value;
    const selected = { ...filters.value };
    if (disposed || !canRead.value) throw new Error('Music library requires a ready session');
    requestedPage.value = page;
    controller?.abort();
    const request = new AbortController();
    controller = request;
    const token = await fetchAndStoreToken();
    if (disposed || request.signal.aborted || !token || identity.value !== key) throw new Error('Music library session or query changed');
    return musicService.getMyMusicList({ page, size: 20, search: selected.search, status: selected.status }, {
      signal: request.signal, musicmintInlineError: true,
    });
  }, {
    pageSize: 20, autoLoad: false, infiniteScroll: false, preserveListOnReload: true,
    requestKey: () => identity.value,
    dataExtractor: response => {
      if (response?.code !== 200) throw new Error('Music library unavailable');
      const list = Array.isArray(response.data) ? response.data : response.data?.list;
      if (response.data == null || (list === null && response.data?.total === 0)) return [];
      if (!Array.isArray(list) || list.some(item => !item || typeof item.id !== 'string' || !item.id || !Number.isFinite(item.status))) throw new Error('Invalid music library response');
      return list;
    },
    totalExtractor: response => {
      const total = response.data?.total ?? response.total ?? 0;
      if (!Number.isFinite(total) || total < 0) throw new Error('Invalid music library total');
      return total;
    },
    errorHandler: () => {},
  });
  const isContinuationError = computed(() => !!pagination.error.value && requestedPage.value > 1 && !!pagination.list.value.length);
  const refresh = async () => { if (!disposed && canRead.value) await pagination.loadData(1); };
  const more = async () => { if (!disposed && canRead.value && !pagination.error.value) await pagination.loadMore(); };
  const applyFilters = (next: LibraryFilters) => {
    if (next.search === filters.value.search && next.status === filters.value.status) return;
    searchInput.value = next.search;
    filters.value = next;
  };
  const submit = () => {
    const search = searchInput.value.trim();
    if (search === filters.value.search) { void refresh(); return; }
    filters.value = { ...filters.value, search };
  };
  const clearFilters = () => { searchInput.value = ''; applyFilters({ search: '', status: 'all' }); };
  watch(identity, (_key, previous) => {
    controller?.abort();
    pagination.reset();
    if (previous) {
      const oldOwner = JSON.parse(JSON.parse(previous)[0])[2];
      if (oldOwner && (oldOwner !== user.value?.id || (isAuthReady.value && !isAuthenticated.value))) {
        searchInput.value = '';
        if (filters.value.search || filters.value.status !== 'all') {
          clearFilters();
          return;
        }
      }
    }
    void refresh();
  }, { immediate: process.client, flush: 'sync' });
  useIntersectionObserver(pagination.loadMoreTrigger, entries => {
    if (entries.some(entry => entry.isIntersecting) && pagination.list.value.length) void more();
  }, { rootMargin: '0px 0px 200px 0px' });
  onScopeDispose(() => { disposed = true; controller?.abort(); });
  return { ...pagination, ownerIdentity, canRead, isAuthReady, searchInput, submittedSearch, status, isContinuationError, submit, clearFilters, applyFilters, refresh, more };
}
